import React, { useMemo } from 'react';
import { Edit2, Trash2, Users, CheckCircle, Clock, Ban, Zap, CalendarClock } from 'lucide-react';

// canEdit and canDelete are passed from TableManagement based on RBAC.
// Defaults to true so any existing usage without these props is unaffected.
export default function TableList({ tables, viewMode, selectedTables, onSelectTables, onEdit, onDelete, canEdit = true, canDelete = true }) {

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      onSelectTables(tables.map(t => t.id));
    } else {
      onSelectTables([]);
    }
  };

  const handleSelectTable = (id) => {
    if (selectedTables.includes(id)) {
      onSelectTables(selectedTables.filter(tid => tid !== id));
    } else {
      onSelectTables([...selectedTables, id]);
    }
  };

  const groupedTables = useMemo(() => {
    const groups = tables.reduce((acc, table) => {
      const sectionKey = table.sectionName || 'Unassigned';
      if (sectionKey.toLowerCase() === 'counter') {
        return acc;
      }
      if (!acc[sectionKey]) {
        acc[sectionKey] = [];
      }
      acc[sectionKey].push(table);
      return acc;
    }, {});

    return Object.keys(groups).map(sectionName => ({
      sectionName,
      tables: groups[sectionName],
    })).sort((a, b) => {
      if (a.sectionName === 'Unassigned') return 1;
      if (b.sectionName === 'Unassigned') return -1;
      return a.sectionName.localeCompare(b.sectionName);
    });
  }, [tables]);

  const getTableVisuals = (table) => {
    const hasReservations = table.reservations && table.reservations.length > 0;
    const blockedReservation = hasReservations ? table.reservations.find(r => r.isBlocked) : null;

    if (blockedReservation) {
        return {
          icon: CalendarClock,
          color: 'bg-blue-100 border-blue-500 text-blue-800',
          hoverColor: 'hover:bg-blue-200',
          statusLabel: 'RESERVED',
          reservation: blockedReservation,
        };
    } else if (table.isOccupied) {
        return {
          icon: Clock,
          color: 'bg-amber-100 border-amber-500 text-amber-800',
          hoverColor: 'hover:bg-amber-200',
          statusLabel: 'OCCUPIED',
          hasUpcoming: hasReservations,
        };
    } else if (table.isActive) {
        return {
          icon: CheckCircle,
          color: 'bg-green-100 border-green-500 text-green-800',
          hoverColor: 'hover:bg-green-200',
          statusLabel: 'READY',
          hasUpcoming: hasReservations,
        };
    } else {
        return {
          icon: Ban,
          color: 'bg-gray-100 border-gray-400 text-gray-700',
          hoverColor: 'hover:bg-gray-200',
          statusLabel: 'CLOSED',
        };
    }
  };

  // --- GRID VIEW ---
  if (viewMode === 'grid') {
    return (
      <div className="w-full space-y-10">
        {groupedTables.map(group => (
          <div
            key={group.sectionName}
            className="w-full bg-white p-6 rounded-xl shadow-inner border-2 border-dashed border-gray-100"
          >
            <h2 className="text-2xl font-extrabold text-gray-900 mb-6 flex items-center gap-3 border-b pb-3">
              <Zap size={24} className="text-teal-600" />
              {group.sectionName}
              <span className="text-base font-medium text-gray-500 ml-auto">({group.tables.length} Tables)</span>
            </h2>

            <div className="flex flex-wrap gap-6 p-4">
              {group.tables.map(table => {
                const { icon: StatusIcon, color, hoverColor, statusLabel, hasUpcoming, reservation } = getTableVisuals(table);
                const isSelected = selectedTables.includes(table.id);
                const shapeClass = table.tableShape === 'round' ? 'rounded-full' : 'rounded-lg';
                return (
                  <div
                    key={table.id}
                    className={`relative w-32 h-32 flex flex-col justify-between items-center p-3 text-center cursor-pointer transition-all duration-200 shadow-md border-4 ${color} ${hoverColor} ${shapeClass} ${
                        isSelected ? 'ring-4 ring-offset-2 ring-blue-500' : 'hover:shadow-lg'
                    }`}
                    onClick={() => handleSelectTable(table.id)}
                    title={`Table ${table.number} - ${table.capacity} Seats - ${statusLabel}${reservation ? ` (${reservation.timeDisplay})` : ''}`}
                  >
                    <div className="w-full flex justify-between items-start">
                        <StatusIcon size={16} className="mt-0.5" />
                        <span className="text-xs font-bold flex items-center bg-white rounded-full px-2 py-0.5 shadow-sm text-gray-700">
                            <Users size={12} className="mr-0.5" />{table.capacity}
                        </span>
                    </div>

                    <div className="text-4xl font-black leading-none">
                        {table.number}
                    </div>

                    {reservation ? (
                      <span className="text-[10px] font-semibold">{reservation.timeDisplay}</span>
                    ) : (
                      <span className="text-xs font-semibold uppercase">{statusLabel}</span>
                    )}

                    {hasUpcoming && !reservation && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center shadow-md">
                        <CalendarClock size={10} className="text-white" />
                      </div>
                    )}

                    {/* Edit overlay — only shown if user can edit */}
                    {canEdit && (
                      <div className={`absolute top-0 left-0 right-0 bottom-0 ${shapeClass} flex items-center justify-center transition-opacity duration-200 ${
                          isSelected ? 'opacity-100 bg-black bg-opacity-20' : 'opacity-0 hover:opacity-100 bg-black bg-opacity-10'
                      }`}>
                          <div className="flex gap-2">
                              <button
                                  onClick={(e) => { e.stopPropagation(); onEdit(table); }}
                                  className="p-2 text-white bg-blue-600 rounded-full hover:bg-blue-700 transition-colors shadow-lg"
                                  title="Edit Table"
                              >
                                  <Edit2 size={16} />
                              </button>
                          </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // --- LIST VIEW ---
  // Only show Actions column if user can edit or delete
  const hasAnyRowAction = canEdit || canDelete;

  return (
    <div className="w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
      <table className="w-full table-auto">
        <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
          <tr>
            <th className="px-4 py-3 text-left w-12">
              <input
                type="checkbox"
                checked={tables.length > 0 && selectedTables.length === tables.length}
                onChange={handleSelectAll}
                className="w-4 h-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
              />
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Number</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Section</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Capacity</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Reservations</th>
            {hasAnyRowAction && (
              <th className="px-4 py-3 text-right w-24 text-xs font-semibold text-gray-700 uppercase">Actions</th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {groupedTables.map(group => (
            <React.Fragment key={group.sectionName}>
              <tr className="bg-gray-100 sticky top-[48px] z-5">
                <td colSpan={hasAnyRowAction ? 7 : 6} className="px-4 py-2 text-sm font-bold text-gray-800 border-y border-gray-300">
                  Section: {group.sectionName}
                </td>
              </tr>
              {group.tables.map(table => (
                <tr key={table.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedTables.includes(table.id)}
                      onChange={() => handleSelectTable(table.id)}
                      className="w-4 h-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-gray-900">{table.number}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {table.sectionName || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    <div className="flex items-center gap-1">
                      <Users size={14} />
                      <span>{table.capacity}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      table.isActive
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {table.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {table.reservations && table.reservations.length > 0 ? (
                      <div className="space-y-1">
                        {table.reservations.slice(0, 2).map((res, idx) => (
                          <div key={idx} className={`flex items-center gap-1.5 text-xs ${res.isBlocked ? 'font-bold text-blue-700' : 'text-gray-600'}`}>
                            <CalendarClock size={12} className={res.isBlocked ? 'text-blue-600' : 'text-gray-400'} />
                            <span>{res.timeDisplay}</span>
                            {res.isBlocked && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-bold">NOW</span>}
                          </div>
                        ))}
                        {table.reservations.length > 2 && (
                          <span className="text-[10px] text-gray-400">+{table.reservations.length - 2} more</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  {/* Actions cell — only rendered if user can edit or delete */}
                  {hasAnyRowAction && (
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {canEdit && (
                          <button
                            onClick={() => onEdit(table)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Edit"
                          >
                            <Edit2 size={16} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => onDelete(table.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}